import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { api } from './api';

// Fetch a resource and refetch on demand. Keeps the last good data while the same
// path reloads; data and errors from another path (the previous record after a
// navigation) are never returned.
export function useApi<T>(path: string | null, deps: unknown[] = []) {
  const [state, setState] = useState<{ path: string | null; data: T | null; error: Error | null }>({ path, data: null, error: null });
  const [loading, setLoading] = useState(!!path);
  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!path) return;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const result = await api<T>(path);
      if (mine === seq.current) setState({ path, data: result, error: null });
    } catch (e) {
      if (mine === seq.current) setState((s) => ({ path, data: s.path === path ? s.data : null, error: e as Error }));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => { void load(); }, [load]);
  const current = state.path === path;
  return { data: current ? state.data : null, error: current ? state.error : null, loading: loading || (!!path && !current), reload: load };
}

// The workspace's currency (sales_policy.currency) for amounts typed into a form.
// Undefined until it loads.
export function useWorkspaceCurrency(): string | undefined {
  return useApi<{ sales_policy?: { currency?: string } }>('/v1/tenant').data?.sales_policy?.currency;
}

// Calls fn every two seconds while `on`: an agent at work, until its answer lands.
export function usePoll(on: boolean, fn: () => void) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => ref.current(), 2000);
    return () => clearInterval(t);
  }, [on]);
}

export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// Names the page in the tab and, after an in-app route change, moves focus to its
// h1 so keyboard and screen-reader users land at the start of the new page. A
// fresh load keeps focus at the top, so Tab reaches the skip link first. Focus
// already inside the page (an autofocused field) is left where it is, and a
// title that changes later on the same page does not move focus again.
let firstPage = true;
export function usePageTitle(title: string | null | undefined) {
  const { pathname } = useLocation();
  const moved = useRef(false);
  useEffect(() => { if (title) pageTitles.set(pathname, title); }, [title, pathname]);
  useEffect(() => {
    if (!title) return;
    document.title = `${title} · Mazu`;
    if (moved.current) return;
    moved.current = true;
    if (firstPage) { firstPage = false; return; }
    const h1 = document.querySelector<HTMLElement>('main h1');
    if (h1 && !h1.closest('main')?.contains(document.activeElement)) { h1.tabIndex = -1; h1.focus({ preventScroll: true }); }
  }, [title]);
}

// The page each history entry was opened from, so a breadcrumb always leads back
// to where the person was. A filter or a redirect on the same screen keeps the
// screen's origin, and so does a page opened from a form (a new or edit page),
// once saved; going back or forward records nothing new.
// shortcut: held in memory, so after a reload a trail is the page's own; keep it in sessionStorage if that bites.
type Origin = { key: string; path: string; pathname: string };
const pageTitles = new Map<string, string>();
const cameFrom = new Map<string, Origin>();
let lastPage: Origin | null = null;

// The app shell calls this on every render, before any page renders.
export function useTrackOrigin() {
  const loc = useLocation();
  const type = useNavigationType();
  if (lastPage?.key === loc.key) return;
  if (lastPage && !cameFrom.has(loc.key)) {
    const sameScreen = type === 'REPLACE' || (type === 'PUSH' && (lastPage.pathname === loc.pathname || /\/(new|edit)$/.test(lastPage.pathname)));
    const from = sameScreen ? cameFrom.get(lastPage.key) : type === 'PUSH' ? lastPage : undefined;
    if (from) cameFrom.set(loc.key, from);
  }
  lastPage = { key: loc.key, path: loc.pathname + loc.search, pathname: loc.pathname };
}

export function useCameFrom(): { to: string; pathname: string; label: string } | null {
  const from = cameFrom.get(useLocation().key);
  const label = from && pageTitles.get(from.pathname);
  return from && label ? { to: from.path, pathname: from.pathname, label } : null;
}

// A per-browser convenience (which calendars are hidden, whether a group is open),
// remembered across visits; storage that throws keeps it for this visit only.
export function useStored<T>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => { try { const s = localStorage.getItem(key); return s === null ? initial : JSON.parse(s) as T; } catch { return initial; } });
  return [v, (next: T) => { setV(next); try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* still applies for this visit */ } }];
}
